/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

/**
 * TabManagerContext - Shared State Management for Code Editor
 *
 * Provides centralized state management for the TabManager component
 * and all its child hooks, sharing editor references, UI configuration,
 * and cache operations for tab content.
 */

// Debug flag for tab modification logging
import * as monaco from 'monaco-editor';
import React, { createContext, useContext, MutableRefObject } from 'react';

import { Tab } from '../helpers/codeEditorHelpers';
import { CachedTab } from '../hooks/types';
import { AstParser } from '../managers/AstParser';

import { Step as UIStep, Story } from '@/pages/projects/LiveAgentTypes';

export const DEBUG_TAB_MODIFICATION = false;

// Type aliases for better readability and maintainability
type EditorRef = MutableRefObject<monaco.editor.IStandaloneCodeEditor | undefined>;
type TabsRef = MutableRefObject<Tab[]>;
type ContentMapRef = MutableRefObject<Map<string, string>>;
type CacheMapRef = MutableRefObject<Map<string, CachedTab>>;
type StringRef = MutableRefObject<string>;
type StepsRef = MutableRefObject<UIStep[]>;
type BooleanRef = MutableRefObject<boolean>;
type VariablesRef = MutableRefObject<{ [key: string]: string }>;
type QueueRef = MutableRefObject<number[]>;

// Function type for caching current tab content and metadata
type CacheCurrentTabFn = (
    editorRef: EditorRef,
    activeTabIdRef: StringRef,
    tabsRef: TabsRef,
    tabContentRef: ContentMapRef,
    tabOriginalContentRef: ContentMapRef,
    getBlockSteps: () => UIStep[],
    enrichBlockSteps: (inputSteps: UIStep[]) => UIStep[],
) => void;

// Function type for updating tab modification status
type UpdateModificationStatusFn = (
    editorRef: EditorRef,
    activeTabIdRef: StringRef,
    tabOriginalContentRef: ContentMapRef,
    setTabs: React.Dispatch<React.SetStateAction<Tab[]>>,
    updateTabsRef: (newTabs: Tab[]) => void,
    tabsRef?: TabsRef,
) => void;

/**
 * Complete context interface containing all shared state and operations
 *
 * Organized into logical groups:
 * 1. Editor & References - Direct Monaco editor access and refs
 * 2. State Management - React state setters and updaters
 * 3. Configuration - AST parser and UI settings
 * 4. Callbacks - Event handlers from parent components
 * 5. Cache Operations - Tab content caching and retrieval
 */
export interface TabManagerContextType {
    // === EDITOR & REFERENCES ===
    /** Direct reference to Monaco editor instance */
    editorRef: EditorRef;
    /** Current active tab ID reference */
    activeTabIdRef: StringRef;
    /** All tabs reference for immediate access */
    tabsRef: TabsRef;
    /** Tab content storage (tabId -> code content) */
    tabContentRef: ContentMapRef;
    /** Original content for modification tracking (tabId -> original code) */
    tabOriginalContentRef: ContentMapRef;
    /** Block cache storage (blockName -> cached data) */
    blockCacheRef: CacheMapRef;

    // === STATE MANAGEMENT ===
    /** React state setter for tabs array */
    setTabs: React.Dispatch<React.SetStateAction<Tab[]>>;
    /** React state setter for active tab ID */
    setActiveTabId: React.Dispatch<React.SetStateAction<string>>;
    /** Helper to update active tab ID in both state and ref */
    updateActiveTabId: (newId: string) => void;
    /** Helper to update tabs ref when state changes */
    updateTabsRef: (newTabs: Tab[]) => void;

    // === CONFIGURATION ===
    /** AST parser for code analysis and syntax validation */
    astParser: AstParser;
    /** Initial code content for main tab */
    initialCode: string;
    /** Current steps/actions in the story (ref for performance) */
    stepsRef: StepsRef;
    /** Whether editor is in disabled/read-only mode (ref for performance) */
    isDisabledRef: BooleanRef;
    /** Whether to hide Playwright-specific features (ref for performance) */
    hidePlaywrightRef: BooleanRef;
    /** Variable substitutions for code highlighting (ref for performance) */
    variablesRef: VariablesRef;
    /** Queue of pending step executions (ref for performance) */
    queueRef: QueueRef;
    /** Whether editor is in read-only mode */
    readOnly: boolean;
    /** Block definitions data for tab creation */
    blocksData?: Story[];

    // === CALLBACKS ===
    /** Callback to execute a step when triggered */
    executeStep?: (step: UIStep) => void;
    /** Callback to show element selector UI */
    showSelector?: () => void;
    /** Callback to highlight selectors in browser */
    highlightSelectors?: (
        selectors: { [key: string]: { selector: string; unique: boolean } } | null,
        force?: boolean,
    ) => void;
    /** Callback when syntax errors are detected */
    onSyntaxError?: (hasError: boolean, messages: string[]) => void;

    // === CACHE OPERATIONS ===
    /** Retrieve cached tab data by key */
    getCachedTab: (key: string) => CachedTab | undefined;
    /** Store tab data in cache */
    setCachedTab: (key: string, cachedTab: CachedTab) => void;
    /** Cache current tab content and metadata before switching */
    cacheCurrentTab: CacheCurrentTabFn;
    /** Update modification status indicators for current tab */
    updateCurrentTabModificationStatus: UpdateModificationStatusFn;

    // === UI UPDATES ===
    /** Trigger component re-render for tab changes */
    triggerRerender?: () => void;
    /** Refresh decorations after ref updates */
    refreshDecorations?: () => void;
    /** Ref to store the actual refresh function */
    refreshDecorationsRef?: React.MutableRefObject<(() => void) | null>;
}

/**
 * React Context for TabManager state
 * Initialized as null and must be provided by TabManagerProvider
 */
const TabManagerContext = createContext<TabManagerContextType | null>(null);

/**
 * Custom hook to safely access TabManager context
 *
 * Throws error if used outside of TabManagerProvider to prevent
 * runtime errors and ensure proper component hierarchy
 *
 * @returns TabManagerContextType - All shared state and operations
 * @throws Error if used outside TabManagerProvider
 */
export const useTabManagerContext = () => {
    const context = useContext(TabManagerContext);
    if (!context) {
        throw new Error('useTabManagerContext must be used within a TabManagerProvider');
    }
    return context;
};

/**
 * Context Provider component
 *
 * Wraps child components and provides access to TabManager state.
 * Should only be used by the main TabManager component.
 *
 * @param children - Child components that need context access
 * @param value - Complete context value with all state and operations
 */
export const TabManagerProvider: React.FC<{
    children: React.ReactNode;
    value: TabManagerContextType;
}> = ({ children, value }) => {
    return <TabManagerContext.Provider value={value}>{children}</TabManagerContext.Provider>;
};
